import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { MigrationStrategies, RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface DeficiencyFiles {
  readonly fileId: string;
  readonly fileName: string;
  readonly fileAimsUUID: string;
  readonly deficiencyUUID: string;
  readonly originalFileName: string;
  readonly path: string;
  readonly legacyId: number;
  readonly isImageFile: boolean;
  readonly deficiencyId: string;
  readonly inspectionId: string;
  readonly showOnReport: boolean;
  readonly fileDescription: string;
  readonly drawingType: number;
  readonly assetId: number;
  readonly fileCategory: number;
  readonly pcsId: string;
  readonly fkAimsfileTblSp?: string;
  readonly sizeMb: number;
  readonly allowSpaccess: boolean;
  readonly siteId: number;
  readonly disciplineId: number;
  readonly userCreated: string;
  readonly userModified?: string;
  readonly extension: string;
  readonly dateCreated?: string;
  readonly dateModified?: string;
  readonly isDelete?: string;
  readonly fileSortOrder?: number;
  readonly assetInspectionTask: number;
  fkInspectorField: number;
  readonly fkInspectorLead: number;
  readonly isReInspectedFile: boolean;
  readonly componentVersionInspectionId?: number;
  readonly componentVersionId?: number;
  readonly fileComponentVersionUUId: string;
  readonly isActiveDeficiencyFile: boolean;
}

export const deficiencyFilesSchema: RxJsonSchema<DeficiencyFiles> = {
  type: 'object',
  version: 1,
  description: 'describes deficiency files',
  primaryKey: 'fileAimsUUID',
  properties: {
    fileId: { type: 'string' },
    fileName: { type: ['string', 'null'] },
    fileAimsUUID: { type: 'string', maxLength: 36 },
    deficiencyUUID: { type: ['string', 'null'] },
    originalFileName: { type: ['string', 'null'] },
    path: { type: ['string', 'null'] },
    legacyId: { type: ['number', 'null'] },
    assetId: { type: ['number', 'null'] },
    isImageFile: { type: ['boolean', 'null'] },
    inspectionId: { type: ['string', 'null'] },
    deficiencyId: { type: ['string', 'null'] },
    showOnReport: { type: ['boolean', 'null'] },
    fileDescription: { type: ['string', 'null'] },
    drawingType: { type: ['number', 'null'] },
    fileCategory: { type: ['number', 'null'] },
    pcsId: { type: ['string', 'null'] },
    fkAimsfileTblSp: { type: ['string', 'null'] },
    sizeMb: { type: ['number', 'null'] },
    allowSpaccess: { type: ['boolean', 'null'] },
    isReInspectedFile: { type: ['boolean', 'null'] },
    siteId: { type: ['number', 'null'] },
    disciplineId: { type: ['number', 'null'] },
    userCreated: { type: ['string', 'null'] },
    userModified: { type: ['string', 'null'] },
    extension: { type: ['string', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    isDelete: { type: ['string', 'null'] },
    fileSortOrder: { type: ['number', 'null'] },
    assetInspectionTask: { type: ['number', 'null'] },
    fkInspectorField: { type: ['number', 'null'] },
    fkInspectorLead: { type: ['number', 'null'] },
    componentVersionInspectionId: { type: ['number', 'null'] },
    componentVersionId: { type: ['number', 'null'] },
    fileComponentVersionUUId: { type: ['string', 'null'] },
    isActiveDeficiencyFile: { type: ['boolean', 'null'] },
  },
};

export const deficiencyFilesMigrationStrategies: MigrationStrategies = {
  1: (oldDoc: DeficiencyFiles) => {
    const newDoc = {
      ...oldDoc,
      assetId: oldDoc.assetId || null,
      isReInspectedFile: oldDoc.isReInspectedFile || false,
      componentVersionInspectionId: oldDoc.componentVersionInspectionId || null,
      componentVersionId: oldDoc.componentVersionId || null,
      isActiveDeficiencyFile: oldDoc.isActiveDeficiencyFile || null,
    };
    return newDoc;
  },
};

export type DeficiencyFilesCollection = RxCollection<DeficiencyFiles> | null;
export type DeficiencyFilesDocument = RxDocument<DeficiencyFiles>;

export const deficiencyFilesPullQueryBuilder = (document: DeficiencyFiles) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query deficiencyFilesFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastModified: DateTime!
      $isLeadInspector: Boolean!
    ) {
      deficiencyFilesFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastModified: $lastModified
        isLeadInspector: $isLeadInspector
      ) {
        fileId
        fileName
        fileAimsUUID
        deficiencyUUID
        originalFileName
        path
        legacyId
        isImageFile
        inspectionId
        deficiencyId
        showOnReport
        fileDescription
        drawingType
        fileCategory
        pcsId
        fkAimsfileTblSp
        sizeMb
        allowSpaccess
        siteId
        disciplineId
        userCreated
        userModified
        isReInspectedFile
        assetId
        extension
        dateCreated
        dateModified
        isDelete
        fileSortOrder
        assetInspectionTask
        fkInspectorField
        fkInspectorLead
        isActiveDeficiencyFile
        componentVersionInspectionId
        componentVersionId
      }
    }
  `);
  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastModified: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.siteId,
        doc.disciplineId,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const deficiencyFilesPushQueryBuilder = (doc: DeficiencyFiles[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation cEDDeficiencyFileInfo(
      $role: Int!
      $MobileDeficiencyFilesFeedInput: MobileDeficiencyFilesFeedInput
    ) {
      cEDDeficiencyFileInfo(role: $role, deficiencyFileRequest: $MobileDeficiencyFilesFeedInput) {
        status
        deficiencyId
        deficiencyUUID
        fileId
        fileUUID
      }
    }
  `);

  const role = getRoleForMutation(document);

  const MobileDeficiencyFilesFeedInput = {
    ...document,
    fkInspectorField: document.fkInspectorField || 0,
    fkInspectorLead: document.fkInspectorLead || 0,
    isReInspectedFile: document.isReInspectedFile || false,
  };

  const variables = {
    MobileDeficiencyFilesFeedInput,
    role,
  };

  return {
    query,
    variables,
  };
};

export const deficiencyFilesPushModifier = (doc: DeficiencyFilesDocument) => {
  const obj: AdditionalFields = {
    ...doc,
  };

  const blackListedProp = ['_meta', 'isDeleted'];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });

  return obj;
};

export default {};
